import React, { useState, useEffect } from 'react';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { Download, MoreVert } from '@mui/icons-material';
import { SessionTypeEnum } from '@motivo/guanyin/src/lookup';
import { useQueryParams } from '@motivo/guanyin/src/hooks/useQueryParams';
import SupervisorTypeFilter from ':src/components/SupervisorTypeFilter';
import useSupervisionModel from ':src/hooks/useSupervisionModel';
import { useLazyQuery, useQuery } from '@apollo/client';
import { DateTime } from 'luxon';
import SnackbarState from ':src/hooks/SnackbarState';
import { downloadFile, downloadCSV } from ':src/utils/downloadUtils';
import { MoSelect, StartsOnSundayLuxonAdapter } from '@motivo/guanyin/src/components';
import { useSelectedProgramFilter } from ':src/domains/programs/SelectedProgramFilterContext';
import { usersByOrganizationId } from ':src/domains/supervisees/queries';
import { useQueryVariablesForSessions } from ':src/hooks/useQueryVariablesForSessions';
import { formatDateRangeForFilename } from '@motivo/guanyin/src/utils/dateUtils';
import {
  fetchAttendanceReport,
  getMinAndMaxDateForParams,
  parseDateParam,
  SessionDateFilter,
} from '../../utils';
import generateSessionCSV from './generateSessionsCSV';
import { sessionsForCSVExportQuery } from '../queries';

type SessionsFiltersProps = {
  minDate?: string;
  maxDate?: string;
  sessionTypeId?: string;
  organizationId?: number;
  shouldShowNotesExport?: boolean;
};
type SessionsFilterVariableProps = Omit<SessionsFiltersProps, 'shouldShowNotesExport'>;

export default function SessionsFilters({
  organizationId,
  shouldShowNotesExport,
}: SessionsFiltersProps) {
  const variables = useQueryVariablesForSessions();
  const { openSnackbar } = SnackbarState.useContainer();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { queryParams, updateQueryParams } = useQueryParams();
  const { isExternalAndInternal } = useSupervisionModel();
  const [programFilter] = useSelectedProgramFilter();
  const [isExportingFile, setIsExportingFile] = useState(false);

  const {
    sessionTypeId: sessionTypeIdParam,
    userId,
    minDate: minDateParam,
    maxDate: maxDateParam,
    sessionDateFilter,
  } = queryParams;
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);

  const minDateFilter = minDateParam ? parseDateParam(minDateParam).toISODate() : undefined;
  const maxDateFilter = maxDateParam ? parseDateParam(maxDateParam).toISODate() : undefined;

  const [queryVariables, setQueryVariables] = useState<SessionsFilterVariableProps>({
    minDate: minDateFilter,
    maxDate: maxDateFilter,
    sessionTypeId: sessionTypeIdParam,
  });

  useEffect(() => {
    setQueryVariables((prev) => ({
      ...prev,
      minDate: minDateFilter,
      maxDate: maxDateFilter,
    }));
  }, [minDateFilter, maxDateFilter]);

  const { data, loading } = useQuery(usersByOrganizationId, {
    variables: {
      // @ts-expect-error TS(2322) FIXME: Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
      organizationId,
      programId: programFilter?.id,
      limit: 999,
      offset: 0,
      isRemovedTab: false,
    },
  });
  const organizationUsers = data?.usersByOrganizationId.result;

  const onFilterMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setIsFilterMenuOpen(true);
  };
  const onFilterMenuClose = () => {
    setAnchorEl(null);
    setIsFilterMenuOpen(false);
  };

  const onExportMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setIsExportMenuOpen(true);
  };

  const onExportMenuClose = () => {
    setAnchorEl(null);
    setIsExportMenuOpen(false);
  };

  const onFilterReset = () => {
    const { minDate: minDateReset, maxDate: maxDateReset } =
      getMinAndMaxDateForParams(sessionDateFilter);
    updateQueryParams(
      {
        sessionTypeId: undefined,
        userId: undefined,
        minDate: minDateReset || undefined,
        maxDate: maxDateReset || undefined,
      },
      true,
    );
  };
  const onApplyFilters = () => {
    onFilterMenuClose();
    const queryParams: Record<string, any> = {
      sessionTypeId: queryVariables.sessionTypeId || '',
      page: '1',
    };

    if (queryVariables.minDate) {
      const minDate = DateTime.fromISO(queryVariables.minDate);
      if (minDate.isValid) {
        queryParams.minDate = minDate.startOf('day').toJSDate().toString();
      }
    }

    if (queryVariables.maxDate) {
      const maxDate = DateTime.fromISO(queryVariables.maxDate);
      if (maxDate.isValid) {
        queryParams.maxDate = maxDate.endOf('day').toJSDate().toString();
      }
    }

    updateQueryParams(queryParams, true);
  };

  const onDownloadNotesPDF = async () => {
    openSnackbar('Generating file...');
    setIsExportingFile(true);

    let startDate: Date | undefined;
    let endDate: Date | undefined;

    if (minDateParam) {
      startDate = new Date(minDateParam);
      if (Number.isNaN(startDate.getTime())) {
        const isoDate = DateTime.fromISO(minDateParam);
        if (isoDate.isValid) {
          startDate = isoDate.toJSDate();
        }
      }
    }

    if (maxDateParam) {
      endDate = new Date(maxDateParam);
      if (Number.isNaN(endDate.getTime())) {
        const isoDate = DateTime.fromISO(maxDateParam);
        if (isoDate.isValid) {
          endDate = isoDate.toJSDate();
        }
      }
    }

    if (!startDate || !endDate) {
      startDate = DateTime.local().startOf('day').minus({ month: 1 }).toJSDate();
      endDate = DateTime.local().endOf('day').toJSDate();
    }

    try {
      const blob = await fetchAttendanceReport({
        startDate,
        endDate,
        userId: Number(userId) || undefined,
        organizationId,
        noShowOnly: sessionDateFilter === SessionDateFilter.MISSED,
        programId: programFilter?.id,
      });

      const dateRangeText = `${DateTime.fromJSDate(startDate).toLocaleString(
        DateTime.DATE_FULL,
      )} to ${DateTime.fromJSDate(endDate).toLocaleString(DateTime.DATE_FULL)}`;
      const fileName = `Motivo session notes ${dateRangeText}`;
      downloadFile(blob, `${fileName}.pdf`);
      openSnackbar('File generated successfully');
    } catch {
      openSnackbar('An error occurred while generating the report');
    } finally {
      setIsExportingFile(false);
    }
  };

  const [fetchAndDownloadSessionCSVData] = useLazyQuery(sessionsForCSVExportQuery, {
    variables: {
      ...variables,
      limit: 10000,
      offset: 0,
    },
    onCompleted(data) {
      if (!isExportingFile) return;
      const csvString = generateSessionCSV(
        data.sessions.result,
        sessionDateFilter === SessionDateFilter.MISSED,
      );
      const startDate = minDateParam || DateTime.now().toISO();
      const dateRange = formatDateRangeForFilename(startDate, maxDateParam);
      const sessionType = sessionDateFilter === SessionDateFilter.MISSED ? 'missed ' : '';
      const fileName = `Motivo ${sessionType}sessions ${dateRange}`;
      downloadCSV(csvString, `${fileName}.csv`);
      setIsExportingFile(false);
    },
  });

  const onDownloadSessionCSV = () => {
    setIsExportingFile(true);
    fetchAndDownloadSessionCSVData();
  };

  const isExportDisabled = isExportingFile;
  const allowExportingPDF =
    sessionDateFilter !== SessionDateFilter.UPCOMING && shouldShowNotesExport;

  if (loading) {
    return null;
  }
  return (
    <LocalizationProvider dateAdapter={StartsOnSundayLuxonAdapter}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" gap={2} alignItems="center">
          <Box width="250px">
            {organizationUsers && (
              <MoSelect
                label="Filter by supervisee"
                name="userId"
                value={userId || ''}
                onChange={(e) => updateQueryParams({ userId: e.target.value as string }, true)}
              >
                <MenuItem value="">All supervisees</MenuItem>
                {organizationUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.fullName}
                  </MenuItem>
                ))}
              </MoSelect>
            )}
          </Box>
          {isExternalAndInternal && (
            <Box width="250px">
              <SupervisorTypeFilter />
            </Box>
          )}
          <Button
            onClick={onFilterMenuOpen}
            variant="text"
            startIcon={<MoreVert />}
            sx={{ fontWeight: 'bold', color: 'primary.main' }}
          >
            More filters
          </Button>
          <Menu
            open={isFilterMenuOpen}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            onClose={onFilterMenuClose}
          >
            <Box display="flex" flexDirection="column" gap={3} p={3}>
              <Typography fontWeight="bold">Date range</Typography>
              <DesktopDatePicker
                label="Start date"
                format="LL/dd/yyyy"
                maxDate={
                  queryVariables.maxDate ? DateTime.fromISO(queryVariables.maxDate) : undefined
                }
                slotProps={{
                  textField: {
                    name: 'minDate',
                    label: 'Start date',
                    fullWidth: true,
                    helperText: null,
                  },
                }}
                onChange={(newDate: DateTime | null) => {
                  if (newDate && newDate.isValid) {
                    setQueryVariables({
                      ...queryVariables,
                      minDate: newDate.toISODate(),
                    });
                  }
                }}
                value={queryVariables.minDate ? DateTime.fromISO(queryVariables.minDate) : null}
              />
              <DesktopDatePicker
                label="End date"
                format="LL/dd/yyyy"
                minDate={
                  queryVariables.minDate ? DateTime.fromISO(queryVariables.minDate) : undefined
                }
                slotProps={{
                  textField: {
                    name: 'maxDate',
                    label: 'End date',
                    fullWidth: true,
                    helperText: null,
                  },
                }}
                onChange={(newDate: DateTime | null) => {
                  if (newDate?.isValid) {
                    setQueryVariables({
                      ...queryVariables,
                      maxDate: newDate.toISODate(),
                    });
                  }
                }}
                value={queryVariables.maxDate ? DateTime.fromISO(queryVariables.maxDate) : null}
              />
              <Divider />
              <Typography fontWeight="bold">Session type</Typography>
              <RadioGroup
                onChange={(e) =>
                  setQueryVariables({
                    ...queryVariables,
                    sessionTypeId: e.target.value as string,
                  })
                }
                value={queryVariables.sessionTypeId || ''}
              >
                <FormControlLabel value="" control={<Radio />} label="All" />
                <FormControlLabel
                  value={SessionTypeEnum.INDIVIDUAL}
                  control={<Radio />}
                  label="Individual"
                />
                <FormControlLabel value={SessionTypeEnum.DYAD} control={<Radio />} label="Dyad" />
                <FormControlLabel value={SessionTypeEnum.GROUP} control={<Radio />} label="Group" />
              </RadioGroup>
              <Box justifyContent="space-between">
                <Button onClick={onFilterReset} color="neutral">
                  Reset options
                </Button>
                <Button variant="outlined" color="neutral" onClick={onApplyFilters}>
                  Apply filter
                </Button>
              </Box>
            </Box>
          </Menu>
        </Box>
        <Box>
          <Menu
            open={isExportMenuOpen}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            onClose={onExportMenuClose}
          >
            <MenuItem onClick={onDownloadSessionCSV}>Sessions - CSV</MenuItem>
            {allowExportingPDF && (
              <MenuItem onClick={onDownloadNotesPDF}>Session notes - PDF</MenuItem>
            )}
          </Menu>
          <Button
            variant="text"
            onClick={onExportMenuOpen}
            disabled={isExportDisabled}
            startIcon={<Download />}
            sx={{ fontWeight: 'bold' }}
          >
            Export
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
